import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: unknown, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: unknown;
  isChunkError: boolean;
  reloadAttempted: boolean;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    if ("message" in error && typeof (error as Record<string, unknown>).message === "string") {
      return (error as Record<string, unknown>).message as string;
    }
    try {
      const json = JSON.stringify(error);
      if (json && json !== "{}") return json;
    } catch {
      // ignore
    }
  }
  return "An unexpected error occurred while rendering this section.";
}

/**
 * Detect if an error is a chunk-load failure (stale service worker cache after deploy).
 * These errors look like:
 *   "error loading dynamically imported module: https://..."
 *   "Loading chunk X failed."
 *   "Failed to fetch dynamically imported module"
 */
function isChunkLoadError(error: unknown): boolean {
  const msg = getErrorMessage(error).toLowerCase();
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("failed to fetch") ||
    msg.includes("importing a module script failed") ||
    msg.includes("error loading")
  );
}

const RELOAD_FLAG_KEY = "eb_chunk_reload_at";

function shouldAutoReload(): boolean {
  try {
    const last = localStorage.getItem(RELOAD_FLAG_KEY);
    if (!last) return true;
    // Allow one auto-reload per 10 seconds to prevent infinite loops
    return Date.now() - parseInt(last, 10) > 10_000;
  } catch {
    return true;
  }
}

function markReload() {
  try {
    localStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false, reloadAttempted: false };
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const chunkError = isChunkLoadError(error);
    return { hasError: true, error, isChunkError: chunkError };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);

    // Auto-reload for chunk errors (stale SW cache after deploy)
    if (isChunkLoadError(error) && shouldAutoReload()) {
      markReload();
      // Clear all SW caches first, then hard reload
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => {
          window.location.reload();
        };
        navigator.serviceWorker.controller.postMessage(
          { type: "CLEAR_ALL_CACHES" },
          [channel.port2]
        );
        // Fallback: reload after 1s even if SW doesn't respond
        setTimeout(() => window.location.reload(), 1000);
      } else {
        window.location.reload();
      }
      this.setState({ reloadAttempted: true });
    }
  }

  handleRetry = () => {
    if (this.state.isChunkError) {
      // For chunk errors, hard reload is more reliable than just resetting state
      markReload();
      window.location.reload();
    } else {
      this.setState({ hasError: false, error: null, isChunkError: false, reloadAttempted: false });
    }
  };

  render() {
    if (this.state.hasError) {
      // If it's a chunk error and we're about to auto-reload, show a loading spinner
      if (this.state.isChunkError && this.state.reloadAttempted) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-teal-600 block mb-3"></i>
              <p className="text-sm text-gray-500">Refreshing page...</p>
            </div>
          </div>
        );
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[300px] flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-rose-500"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-sm text-gray-500 mb-4">
              {this.state.isChunkError
                ? "A page resource failed to load. This usually happens after an update — reloading will fix it."
                : getErrorMessage(this.state.error)}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className="text-sm bg-teal-600 hover:bg-teal-500 text-white px-4 py-2 rounded-lg font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-refresh-line"></i>
                {this.state.isChunkError ? "Reload Page" : "Retry"}
              </button>
              {!this.state.isChunkError && (
                <button
                  onClick={() => window.location.reload()}
                  className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2 cursor-pointer whitespace-nowrap"
                >
                  Reload Page
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}